// Property field validation limits and constraints
export const PROPERTY_FIELD_LIMITS = {
    // Numeric field limits
    BEDS_MIN: 0,
    BEDS_MAX: 20,

    BATHS_MIN: 0,
    BATHS_MAX: 20,
    BATHS_STEP: 0.5, // Allow half baths (e.g., 1.5, 2.5)

    GARAGES_MIN: 0,
    GARAGES_MAX: 10,

    GARAGE_SPACES_MIN: 0,
    GARAGE_SPACES_MAX: 10,

    CARPORTS_MIN: 0,
    CARPORTS_MAX: 10,

    GARAGE_SIZE_MIN: 0,
    GARAGE_SIZE_MAX: 10000, // square feet

    PRICE_MIN: 0,
    PRICE_MAX: 999999999, // $999,999,999

    LIVING_AREA_MIN: 0,
    LIVING_AREA_MAX: 100000, // square feet

    FULL_BATHROOMS_MIN: 0,
    FULL_BATHROOMS_MAX: 20,

    HALF_BATHROOMS_MIN: 0,
    HALF_BATHROOMS_MAX: 20,

    // Text field limits
    DESCRIPTION_MAX_LENGTH: 5000,
    ADDRESS_MAX_LENGTH: 500,
    NAME_MAX_LENGTH: 200,
};

/**
 * Validates a numeric value against specified min/max constraints
 * @param {number|string} value - The value to validate
 * @param {number} min - Minimum allowed value
 * @param {number} max - Maximum allowed value
 * @param {boolean} allowDecimals - Whether to allow decimal values
 * @returns {object} - { isValid: boolean, error: string|null, sanitizedValue: number|null }
 */
export const validateNumericField = (value, min, max, allowDecimals = false) => {
    // Handle empty values
    if (value === '' || value === null || value === undefined) {
        return { isValid: true, error: null, sanitizedValue: null };
    }

    const numValue = Number(value);

    // Check if it's a valid number
    if (isNaN(numValue)) {
        return {
            isValid: false,
            error: 'Please enter a valid number',
            sanitizedValue: null
        };
    }

    // Check for decimals if not allowed
    if (!allowDecimals && !Number.isInteger(numValue)) {
        return {
            isValid: false,
            error: 'Please enter a whole number',
            sanitizedValue: Math.floor(numValue)
        };
    }

    // Check minimum value
    if (numValue < min) {
        return {
            isValid: false,
            error: `Value must be at least ${min}`,
            sanitizedValue: min
        };
    }

    // Check maximum value
    if (numValue > max) {
        return {
            isValid: false,
            error: `Value must not exceed ${max}`,
            sanitizedValue: max
        };
    }

    return { isValid: true, error: null, sanitizedValue: numValue };
};

/**
 * Validates beds field
 */
export const validateBeds = (value) => {
    return validateNumericField(
        value,
        PROPERTY_FIELD_LIMITS.BEDS_MIN,
        PROPERTY_FIELD_LIMITS.BEDS_MAX,
        false // Integer only
    );
};

/**
 * Validates baths field
 */
export const validateBaths = (value) => {
    const result = validateNumericField(
        value,
        PROPERTY_FIELD_LIMITS.BATHS_MIN,
        PROPERTY_FIELD_LIMITS.BATHS_MAX,
        true // Allow decimals for half baths
    );

    // Additional validation for step (0.5 increments)
    if (result.isValid && result.sanitizedValue !== null) {
        const numValue = Number(value);
        // Check if value is in 0.5 increments (0, 0.5, 1, 1.5, etc.)
        if ((numValue * 2) % 1 !== 0) {
            return {
                isValid: false,
                error: 'Bathrooms must be in 0.5 increments (e.g., 1.5, 2.5)',
                sanitizedValue: Math.round(numValue * 2) / 2
            };
        }
    }

    return result;
};

/**
 * Validates garages field
 */
export const validateGarages = (value) => {
    return validateNumericField(
        value,
        PROPERTY_FIELD_LIMITS.GARAGES_MIN,
        PROPERTY_FIELD_LIMITS.GARAGES_MAX,
        false // Integer only
    );
};

/**
 * Validates price field
 */
export const validatePrice = (value) => {
    return validateNumericField(
        value,
        PROPERTY_FIELD_LIMITS.PRICE_MIN,
        PROPERTY_FIELD_LIMITS.PRICE_MAX,
        true // Allow decimals for cents
    );
};

/**
 * Validates living area field
 */
export const validateLivingArea = (value) => {
    return validateNumericField(
        value,
        PROPERTY_FIELD_LIMITS.LIVING_AREA_MIN,
        PROPERTY_FIELD_LIMITS.LIVING_AREA_MAX,
        true // Allow decimals
    );
};

/**
 * Get validation error message for a field
 */
export const getValidationMessage = (fieldName, min, max, allowDecimals = false) => {
    const numberType = allowDecimals ? 'number' : 'whole number';
    return `Please enter a ${numberType} between ${min} and ${max}`;
};

export default PROPERTY_FIELD_LIMITS;
